import React from "react";
import { Progress, UncontrolledTooltip } from "reactstrap";
import { FormFieldConfig } from "Components/Common/CommonForm";
import TruncatedText from "Components/Common/TruncatedText";
import ip from "../../IPinventory/css/ipInventory.module.scss";
import { formatDate } from "../../../../common/service/CommonService";
import { getIPCategoryLabel } from "modules/Pharmacy/IPinventory/helpers/ipInventory.constant";
import { validations } from "validations/FormValidations";
import { LineClearanceFieldItem } from "./lineClearance.types";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";

// Section 1: Line Clearance Details fields
export const getHeaderFields = (
  studyOptions: any[],
  periodOptions: any[]
) => {
  const headerFields: FormFieldConfig[] = [
    {
      name: "studycode",
      label: "Study Code",
      type: "select",
      required: true,
      options: studyOptions,
      disabled: true,
      placeholder: "Select Study",
    },
    {
      name: "productname",
      label: "Product Name",
      type: "text",
      placeholder: "Select IP Category ",
      disabled: true,
    },
    {
      name: "period",
      label: "Period",
      type: "select",
      required: true,
      placeholder: "Select Period",
      options: periodOptions,
    },
    {
      name: "reasonforclearance",
      label: "Reason for Line Clearance",
      type: "text",
      required: true,
      placeholder: "e.g. Changeover for next study",
      rules: validations.lineclearance.reasonforclearance,
    },
    {
      name: "clearancedate",
      label: "Date of Line Clearance",
      type: "flatpickr",
      required: true,
      optionsProps: {
        maxDate: "today",
      },
    },
    {
      name: "clearancetime",
      label: "Time of Line Clearance",
      type: "time",
      required: true,
    },
  ];

  return headerFields;
};

// IP Category field — rendered separately
export const getIpCategoryFields = (
  selectedStudyCode: string,
  ipCategoryOptions: any[]
) => {
  const ipCategoryFields: FormFieldConfig[] = [
    {
      name: "ipcategory",
      label: "Type of Investigational Product",
      type: "radio",
      required: true,
      disabled: !selectedStudyCode,
      options: ipCategoryOptions,
    },
  ];
  return ipCategoryFields;
};

// Section 3: Declaration & Verification fields
export const pharmacistDeclarationFields: FormFieldConfig[] = [
  {
    name: "decisionofa",
    label: "Line Clearance performed by (A) (Pharmacist / Designee)",
    type: "radio",
    required: true,
    options: [
      {
        label: "The line is clear and can proceed for next operation.",
        value: "yes",
      },
      {
        label: "The line is not clear and cannot proceed for next operation.",
        value: "no",
      },
    ],
  },
];

export const qaDeclarationFields: FormFieldConfig[] = [
  {
    name: "decisionofb",
    label: "Line Clearance verified by (B) (QA Person)",
    type: "radio",
    required: true,
    options: [
      {
        label: "The line is clear and can proceed for next operation.",
        value: "yes",
      },
      {
        label: "The line is not clear and cannot proceed for next operation.",
        value: "no",
      },
    ],
  },
];

// Table Columns for Line Clearance List
export const getLineClearanceColumns = (
  handleViewClick?: (row: any) => void,
  hasPermission?:any
): any[] => [
  {
    key: "studycode",
    label: "STUDY CODE",
    type: "link",
    onClick: (row: any) => hasPermission(PERMISSION_MODULES.LINE_CLEARANCE,PERMISSION_ACTIONS.VIEW)?handleViewClick?.(row):null,
    render: (_: any, row?: any) => {
      if (!row) return null;
      if(!hasPermission(PERMISSION_MODULES.LINE_CLEARANCE,PERMISSION_ACTIONS.VIEW)){
        return (
          <div>
            <p className="fw-semibold text-dark mb-0">{row.studycode || "—"}</p>
          </div>
        );
      }
      else {
        return (
          <div>
            <p className="fw-semibold text-primary mb-0">{row.studycode || "—"}</p>
          </div>
        );
      }
    },
  },
  {
    key: "periodnumber",
    label: "PERIOD NUMBER",
    textCenter: true,
    render: (_: any, row?: any) => {
      if (!row) return null;
      return (
        <p className="fw-medium text-dark mb-0">
          {row.period_number || "—"}
        </p>
      );
    },
  },
  {
    key: "productname",
    label: "PRODUCT NAME",
    render: (_: any, row?: any) => {
      if (!row) return null;
      return (
        <div>
          <p className="fw-medium text-dark mb-0">
            <TruncatedText text={row.productname || "—"} maxWidth="120px" />
          </p>
        </div>
      );
    },
  },
  {
    key: "ipcategory",
    label: "TYPE OF IP",
    render: (_: any, row?: any) => {
      if (!row) return null;
      return (
        <p className="fw-medium text-dark mb-0">
          {getIPCategoryLabel(row.ipcategory) || "—"}
        </p>
      );
    },
  },
  {
    key: "clearancedate",
    label: "DATE OF LINE CLEARANCE",
    textCenter: true,
    render: (_: any, row?: any) => {
      if (!row) return null;
      return (
        <p className="fw-medium text-dark mb-0">
          {formatDate(row.clearancedate) || "—"}
        </p>
      );
    },
  },
  {
    key: "clearancetime",
    label: "TIME OF LINE CLEARANCE",
    textCenter: true,
    render: (_: any, row?: any) => {
      if (!row) return null;
      return (
        <p className="fw-medium text-dark mb-0">
          {row.clearancetime || "—"}
        </p>
      );
    },
  },
  {
    key: "verifiedby",
    label: "VERIFIED BY",
    render: (_: any, row?: any) => {
      if (!row) return null;
      return (
        <>
          <p className={ip.cellPrimary}>{row.verifiedbyname || row.verifiedby || "-"}</p>
          <p className={ip.cellSecondary}>
            {row.verifieddate ? formatDate(row.verifieddate) : "-"}
          </p>
        </>
      );
    },
  },
  {
    key: "progress",
    label: "PROGRESS",
    textCenter: true,
    render: (_: any, row?: any) => {
      if (!row) return null;
      const targetId = `lc-status-info-${String(row?.lineclearanceid)}`;

      const steps = [
        {
          title: "Pharmacist Clearance",
          isCompleted: Boolean(row?.lineclearanceid),
        },
        {
          title: "QA Verification",
          isCompleted: Boolean(row?.isreviewed),
        },
        {
          title: "Verification",
          isCompleted: Boolean(row?.isverified),
        },
      ];

      const completedCount = steps.filter((s) => s.isCompleted).length;
      const totalCount = steps.length;
      const percentage = Math.round((completedCount / totalCount) * 100);

      let progressColor = "danger";
      if (percentage === 100) {
        progressColor = "success";
      } else if (percentage >= 66) {
        progressColor = "info";
      } else if (percentage >= 33) {
        progressColor = "warning";
      }

      return (
        <>
          <div
            id={targetId}
            className="d-flex flex-column align-items-center justify-content-center mx-auto"
            style={{ cursor: "pointer", width: "85px" }}
          >
            <div
              className="d-flex align-items-center justify-content-between w-100 mb-1"
              style={{ fontSize: "10px", lineHeight: 1 }}
            >
              <span className="fw-medium text-muted">
                {completedCount}/{totalCount}
              </span>
              <span className={`fw-bold text-${progressColor}`}>{percentage}%</span>
            </div>
            <Progress
              value={percentage}
              color={progressColor}
              style={{ height: "6px", width: "100%", borderRadius: "3px" }}
            />
          </div>
          <UncontrolledTooltip
            placement="left"
            target={targetId}
            innerClassName="mw-100 p-2"
            style={{ pointerEvents: "none", maxWidth: "250px" }}
          >
            <div
              className="d-flex flex-column text-start px-1 py-1"
              style={{ fontSize: "12px", pointerEvents: "none" }}
            >
              {steps.map((step, idx) => (
                <React.Fragment key={idx}>
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{ width: "18px" }}
                    >
                      {step.isCompleted ? (
                        <i className="ri-check-line text-white fw-bold fs-15" />
                      ) : (
                        <i className="ri-checkbox-blank-circle-line text-warning fs-13" />
                      )}
                    </div>
                    <span
                      className={step.isCompleted ? "fw-medium text-white" : "text-white-50"}
                      style={{ whiteSpace: "nowrap" }}
                    >
                      {step.title}
                    </span>
                  </div>
                  {idx < steps.length - 1 && (
                    <div
                      style={{ width: "18px", height: "14px" }}
                      className="d-flex align-items-center justify-content-start"
                    >
                      <div
                        style={{
                          width: "2px",
                          height: "100%",
                          backgroundColor: step.isCompleted
                            ? "#20c997"
                            : "rgba(255, 255, 255, 0.4)",
                          marginLeft: "8px",
                          borderRadius: "1px",
                        }}
                      />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </UncontrolledTooltip>
        </>
      );
    },
  },
];

// Record Summary fields for QA Step 2 View (matching IP Inventory pattern)
export const getLineClearanceRecordFields = (selectedRecord: any): LineClearanceFieldItem[] => {
  if (!selectedRecord) return [];

  const decisionofa =
    selectedRecord?.performerdecision !== undefined &&
    selectedRecord?.performerdecision !== null
      ? selectedRecord?.performerdecision
        ? "yes"
        : "no"
      : "—";

  return [
    { label: "Study Code", value: selectedRecord.studycode || "—" },
    { label: "Product Name", value: selectedRecord.productname || "—" },
    { label: "Period", value: selectedRecord.period_number || "—" },
    { label: "Reason for Line Clearance", value: selectedRecord.reasonforclearance || "—" },
    {
      label: "Clearance Date",
      value: selectedRecord.clearancedate
        ? formatDate(selectedRecord.clearancedate)
        : "—",
    },
    { label: "Clearance Time", value: selectedRecord.clearancetime || "—" },
    { label: "IP Category", value: selectedRecord.ipcategory || "—" },
    {
      label: "Pharmacist Decision",
      value:
        decisionofa === "yes" ? (
          <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1">
            Line Clear
          </span>
        ) : decisionofa === "no" ? (
          <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-2 py-1">
            Line Not Clear
          </span>
        ) : (
          "—"
        ),
    },
  ];
};
